(() => {
  const instances = new Map();

  function initializeCardRail(root) {
    const viewport = root.querySelector('[data-review-viewport]');
    const track = root.querySelector('[data-review-track]');
    const slides = Array.from(track?.querySelectorAll('[data-review-slide]') || []);
    const controls = root.querySelector('[data-review-controls]');
    const dots = Array.from(root.querySelectorAll('[data-review-dot]'));
    const status = root.querySelector('[data-review-status]');
    if (!viewport || !track || !slides.length) return;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const count = slides.length;
    const itemLabel = root.dataset.reviewLabel || "Bilde";
    let activeIndex = -1;
    let navigationTarget = null;
    let scrollFrame;
    let resizeFrame;
    let heightFrame;
    let motionFrame;
    let dragFrame;
    let cardMetrics = [];
    let trackInset = 0;
    let trackPadding = 0;
    let viewportHeight = 0;
    let resizeNeedsAlignment = false;
    let settleTimer;
    let pointer;
    let suppressClick = false;
    let clickResetTimer;
    const expansions = [];

    // Every original card stays available to reading, keyboard focus and touch.
    slides.forEach((slide) => {
      slide.removeAttribute('aria-hidden');
      slide.inert = false;
    });
    root.classList.add('is-ready');
    if (controls) controls.hidden = count < 2;

    const characterLimit = Math.max(1, parseInt(root.dataset.reviewCharacterLimit, 10) || 220);
    slides.forEach((slide) => {
      const text = slide.querySelector('[data-review-text]');
      const button = slide.querySelector('[data-review-expand]');
      if (!text || !button) return;
      const originalNodes = Array.from(text.childNodes).map((node) => node.cloneNode(true));
      const fullText = text.textContent.replace(/\s+/g, ' ').trim();
      if (fullText.length <= characterLimit) return;
      let excerpt = '';
      // Leave room for the ellipsis without splitting an emoji or other surrogate pair.
      for (const character of fullText) {
        if (excerpt.length + character.length > characterLimit - 1) break;
        excerpt += character;
      }
      if (!/^\s/.test(fullText.slice(excerpt.length)) && !/\s$/.test(excerpt)) {
        const boundary = excerpt.lastIndexOf(' ');
        if (boundary > 0) excerpt = excerpt.slice(0, boundary);
      }
      excerpt = `${excerpt.trimEnd()}…`;
      const originalButtonText = button.textContent;
      const originalHidden = button.hidden;
      const originalExpanded = button.getAttribute('aria-expanded');
      let expanded = false;
      text.textContent = excerpt;
      button.hidden = false;
      button.textContent = "Se mer";
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        expanded = !expanded;
        if (expanded) text.replaceChildren(...originalNodes.map((node) => node.cloneNode(true)));
        else text.textContent = excerpt;
        button.textContent = expanded ? "Se mindre" : "Se mer";
        button.setAttribute('aria-expanded', String(expanded));
        scheduleHeight();
      }, options);
      expansions.push(() => {
        text.replaceChildren(...originalNodes.map((node) => node.cloneNode(true)));
        button.textContent = originalButtonText;
        button.hidden = originalHidden;
        if (originalExpanded === null) button.removeAttribute('aria-expanded');
        else button.setAttribute('aria-expanded', originalExpanded);
      });
    });

    function maximumScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function measureCards() {
      if (controls) controls.hidden = count < 2 || maximumScroll() < 1;
      const viewportLeft = viewport.getBoundingClientRect().left + viewport.clientLeft;
      const padding = window.getComputedStyle(track);
      trackInset = parseFloat(padding.paddingInlineStart) || 0;
      trackPadding = (parseFloat(padding.paddingTop) || 0) + (parseFloat(padding.paddingBottom) || 0);
      cardMetrics = slides.map((slide) => {
        const rectangle = slide.getBoundingClientRect();
        return { left: rectangle.left - viewportLeft + viewport.scrollLeft, width: rectangle.width, height: rectangle.height };
      });
    }

    function updateViewportHeight() {
      resetVerticalScroll();
      const viewportLeft = viewport.scrollLeft;
      const viewportRight = viewportLeft + viewport.clientWidth;
      let visibleHeight = 0;
      cardMetrics.forEach((rectangle) => {
        const overlap = Math.min(rectangle.left + rectangle.width, viewportRight) - Math.max(rectangle.left, viewportLeft);
        // A thin edge of the next card should not create a tall empty footer.
        if (overlap >= Math.min(40, rectangle.width * 0.15)) {
          visibleHeight = Math.max(visibleHeight, rectangle.height);
        }
      });
      if (!visibleHeight) visibleHeight = cardMetrics[Math.max(0, activeIndex)]?.height || 0;
      let height = Math.ceil(visibleHeight + trackPadding);
      // Grow enough to reveal incoming cards, then shrink once the gesture ends.
      // Repeated shrinking while panning makes the whole page bob up and down.
      if (viewport.classList.contains('is-scrolling')) height = Math.max(height, viewportHeight);
      if (height !== viewportHeight) {
        viewportHeight = height;
        viewport.style.height = `${height}px`;
      }
    }

    function resetVerticalScroll() {
      if (!viewport.scrollTop) return;
      // Focusing a control can scroll an overflow-hidden axis. Keep the row at
      // its top without moving the page or changing the current horizontal offset.
      const left = viewport.scrollLeft;
      viewport.scrollTo({ left, top: 0, behavior: 'instant' });
    }

    function scheduleHeight() {
      if (heightFrame !== undefined) return;
      heightFrame = window.requestAnimationFrame(() => {
        heightFrame = undefined;
        updateViewportHeight();
      });
    }

    function cardPosition(index) {
      return cardMetrics[index].left - trackInset;
    }

    function nearestIndex() {
      const maximum = maximumScroll();
      // At the physical end, the final group is visible without an empty runway.
      if (maximum > 0 && viewport.scrollLeft >= maximum - 1) return count - 1;
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const candidate = Math.abs(cardPosition(index) - viewport.scrollLeft);
        if (candidate < distance) {
          nearest = index;
          distance = candidate;
        }
      });
      return nearest;
    }

    function paint(index, announce = false) {
      if (index === activeIndex) {
        if (announce && status) status.textContent = `${itemLabel} ${index + 1} av ${count}`;
        return;
      }
      activeIndex = index;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (announce && status) status.textContent = `${itemLabel} ${index + 1} av ${count}`;
    }

    function finishScroll() {
      window.clearTimeout(settleTimer);
      if (motionFrame !== undefined || pointer?.dragging) return;
      viewport.classList.remove('is-scrolling');
      // The last few dots share a clamped scroll position. Keep the chosen dot
      // selected; its card remains visible alongside the other final cards.
      paint(navigationTarget === null ? nearestIndex() : navigationTarget, true);
      scheduleHeight();
    }

    function scheduleSettle() {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(finishScroll, 180);
    }

    function move(index, animate = true, announce = true) {
      const nextIndex = (index + count) % count;
      stopMotion();
      window.clearTimeout(settleTimer);
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = undefined;
      if (viewport.contains(document.activeElement) && document.activeElement !== viewport) {
        viewport.focus({ preventScroll: true });
      }
      const left = Math.max(0, Math.min(maximumScroll(), cardPosition(nextIndex)));
      paint(nextIndex, announce);
      navigationTarget = nextIndex;
      if (Math.abs(left - viewport.scrollLeft) < 1) {
        finishScroll();
        return;
      }
      if (!animate || reducedMotion.matches) {
        viewport.scrollTo({ left, top: 0, behavior: 'instant' });
        finishScroll();
        return;
      }
      const startLeft = viewport.scrollLeft;
      const started = window.performance.now();
      const duration = Math.min(650, 380 + Math.abs(left - startLeft) * 0.18);
      viewport.classList.add('is-scrolling');
      const step = (now) => {
        const progress = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        viewport.scrollTo({ left: startLeft + (left - startLeft) * eased, top: 0, behavior: 'instant' });
        if (progress < 1) motionFrame = window.requestAnimationFrame(step);
        else {
          motionFrame = undefined;
          finishScroll();
        }
      };
      motionFrame = window.requestAnimationFrame(step);
    }

    function stopMotion() {
      window.cancelAnimationFrame(motionFrame);
      motionFrame = undefined;
    }

    function beginManualScroll() {
      stopMotion();
      navigationTarget = null;
      window.clearTimeout(settleTimer);
      viewport.classList.add('is-scrolling');
      paint(nearestIndex());
      scheduleSettle();
    }

    function coast(velocity) {
      let previous = window.performance.now();
      const started = previous;
      viewport.classList.add('is-scrolling');
      const step = (now) => {
        const elapsed = Math.min(32, now - previous);
        previous = now;
        const decay = Math.exp(-elapsed / 210);
        const left = viewport.scrollLeft;
        const next = Math.max(0, Math.min(maximumScroll(), left + velocity * 210 * (1 - decay)));
        velocity *= decay;
        viewport.scrollTo({ left: next, top: 0, behavior: 'instant' });
        if (Math.abs(velocity) > 0.035 && Math.abs(next - left) > 0.05 && now - started < 1000) {
          motionFrame = window.requestAnimationFrame(step);
        } else {
          motionFrame = undefined;
          finishScroll();
        }
      };
      motionFrame = window.requestAnimationFrame(step);
    }

    function interactiveTarget(target) {
      return target.closest?.('a, button, input, textarea, select, summary, [contenteditable="true"], [role="button"]');
    }

    function finishPointer(event) {
      if (!pointer || (event && event.pointerId !== pointer.id)) return;
      const ended = pointer;
      pointer = null;
      window.cancelAnimationFrame(dragFrame);
      dragFrame = undefined;
      if (ended.dragging) viewport.scrollTo({ left: ended.nextLeft, top: 0, behavior: 'instant' });
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(ended.id)) viewport.releasePointerCapture(ended.id);
      if (!ended.dragging) {
        finishScroll();
        return;
      }
      suppressClick = true;
      window.clearTimeout(clickResetTimer);
      clickResetTimer = window.setTimeout(() => { suppressClick = false; }, 0);
      paint(nearestIndex());
      const cancelled = event?.type === 'pointercancel' || event?.type === 'lostpointercapture';
      if (!cancelled && !reducedMotion.matches && window.performance.now() - ended.lastTime < 90 && Math.abs(ended.velocity) > 0.08) coast(ended.velocity);
      else finishScroll();
    }

    root.querySelector('[data-review-prev]')?.addEventListener('click', () => move(activeIndex - 1), options);
    root.querySelector('[data-review-next]')?.addEventListener('click', () => move(activeIndex + 1), options);
    dots.forEach((dot, index) => dot.addEventListener('click', () => move(index), options));
    root.addEventListener('keydown', (event) => {
      if (count < 2 || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') move(activeIndex - 1);
      if (event.key === 'ArrowRight') move(activeIndex + 1);
      if (event.key === 'Home') move(0);
      if (event.key === 'End') move(count - 1);
    }, options);
    viewport.addEventListener('scroll', () => {
      viewport.classList.add('is-scrolling');
      if (scrollFrame === undefined) scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = undefined;
          resetVerticalScroll();
          if (navigationTarget === null) paint(nearestIndex());
          updateViewportHeight();
        });
      scheduleSettle();
    }, { ...options, passive: true });
    viewport.addEventListener('scrollend', finishScroll, options);
    viewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) return;
      // Native touch panning keeps the browser's momentum and vertical gesture handling.
      if (event.pointerType !== 'mouse') {
        beginManualScroll();
        return;
      }
      if (event.button !== 0) return;
      if (interactiveTarget(event.target)) {
        stopMotion();
        scheduleSettle();
        return;
      }
      beginManualScroll();
      const now = window.performance.now();
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, nextLeft: viewport.scrollLeft, lastX: event.clientX, lastTime: now, velocity: 0, dragging: false };
    }, { ...options, passive: true });
    window.addEventListener('pointermove', (event) => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (!pointer.dragging) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          finishPointer(event);
          return;
        }
        pointer.dragging = true;
        viewport.setPointerCapture(pointer.id);
        viewport.classList.add('is-dragging');
      }
      event.preventDefault();
      const now = window.performance.now();
      const elapsed = Math.max(1, now - pointer.lastTime);
      const velocity = Math.max(-2.5, Math.min(2.5, (pointer.lastX - event.clientX) / elapsed));
      pointer.velocity = elapsed > 80 ? velocity : pointer.velocity * 0.3 + velocity * 0.7;
      pointer.lastX = event.clientX;
      pointer.lastTime = now;
      pointer.nextLeft = Math.max(0, Math.min(maximumScroll(), pointer.left - dx));
      if (dragFrame === undefined) dragFrame = window.requestAnimationFrame(() => {
        dragFrame = undefined;
        if (pointer) viewport.scrollTo({ left: pointer.nextLeft, top: 0, behavior: 'instant' });
      });
    }, options);
    window.addEventListener('pointerup', finishPointer, options);
    window.addEventListener('pointercancel', finishPointer, options);
    viewport.addEventListener('lostpointercapture', finishPointer, options);
    viewport.addEventListener('dragstart', (event) => {
      if (pointer) event.preventDefault();
    }, options);
    viewport.addEventListener('click', (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { ...options, capture: true });
    viewport.addEventListener('wheel', (event) => {
      if (event.deltaX || (event.shiftKey && event.deltaY)) beginManualScroll();
    }, { ...options, passive: true });
    viewport.addEventListener('focusin', () => {
      // Let the browser reveal a newly focused card control; do not interrupt
      // its horizontal focus scroll with the previous navigation target.
      navigationTarget = null;
      stopMotion();
      window.clearTimeout(settleTimer);
      paint(nearestIndex());
      scheduleHeight();
    }, options);
    root.addEventListener('shopify:block:select', (event) => {
      const index = slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (index >= 0) move(index);
    }, options);

    let observedWidth = viewport.clientWidth;
    const observer = new ResizeObserver((entries) => {
      if (observedWidth !== viewport.clientWidth || entries.some((entry) => entry.target !== viewport)) {
        resizeNeedsAlignment ||= observedWidth !== viewport.clientWidth;
        observedWidth = viewport.clientWidth;
        if (resizeFrame !== undefined) return;
        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = undefined;
          measureCards();
          if (resizeNeedsAlignment) move(Math.max(0, activeIndex), false, false);
          resizeNeedsAlignment = false;
          scheduleHeight();
        });
      }
    });
    observer.observe(viewport);
    slides.forEach((slide) => observer.observe(slide));
    measureCards();
    paint(nearestIndex());
    updateViewportHeight();
    instances.set(root, () => {
      controller.abort();
      observer.disconnect();
      window.clearTimeout(settleTimer);
      window.clearTimeout(clickResetTimer);
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(resizeFrame);
      window.cancelAnimationFrame(heightFrame);
      window.cancelAnimationFrame(dragFrame);
      stopMotion();
      if (pointer && viewport.hasPointerCapture(pointer.id)) viewport.releasePointerCapture(pointer.id);
      pointer = null;
      viewport.classList.remove('is-dragging', 'is-scrolling');
      viewport.style.removeProperty('height');
      expansions.forEach((restore) => restore());
      slides.forEach((slide) => slide.classList.remove('is-active'));
      dots.forEach((dot) => {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      });
      root.classList.remove('is-ready');
      if (controls) controls.hidden = true;
      instances.delete(root);
    });
  }

  function initialize(root) {
    if (instances.has(root)) return;
    if (root.classList.contains('wp-reviews--cards') || root.dataset.reviewCompact === 'true') {
      initializeCardRail(root);
      return;
    }
    const viewport = root.querySelector('[data-review-viewport]');
    const track = root.querySelector('[data-review-track]');
    const slides = Array.from(track?.querySelectorAll('[data-review-slide]') || []);
    const controls = root.querySelector('[data-review-controls]');
    const dots = Array.from(root.querySelectorAll('[data-review-dot]'));
    const status = root.querySelector('[data-review-status]');
    if (!viewport || !track || !slides.length) return;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const count = slides.length;
    const itemLabel = root.dataset.reviewLabel || "Bilde";
    let position = count > 1 ? 1 : 0;
    let timer;
    let pointer;
    let swiped = false;
    let resizeFrame;

    function clone(slide) {
      const copy = slide.cloneNode(true);
      copy.removeAttribute('id');
      copy.removeAttribute('data-review-slide');
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      copy.setAttribute('data-review-clone', '');
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      return copy;
    }

    if (count > 1) {
      track.prepend(clone(slides[count - 1]));
      track.append(clone(slides[0]));
    }
    const cards = Array.from(track.children);
    root.classList.add('is-ready');
    controls.hidden = count < 2;

    function logicalIndex() {
      return count > 1 ? (position - 1 + count) % count : 0;
    }

    function paint(animate = false, announce = false) {
      const index = logicalIndex();
      const card = cards[position];
      if (root.classList.contains('wp-reviews--photos')) viewport.style.height = `${card.offsetHeight}px`;
      track.style.transition = animate && !reducedMotion.matches ? '' : 'none';
      track.style.transform = `translateX(${(viewport.clientWidth - card.offsetWidth) / 2 - card.offsetLeft}px)`;
      cards.forEach((item, cardIndex) => item.classList.toggle('is-active', cardIndex === position));
      slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', String(slideIndex !== index));
        slide.inert = slideIndex !== index;
      });
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (announce && status) status.textContent = `${itemLabel} ${index + 1} av ${count}`;
    }

    function settle() {
      window.clearTimeout(timer);
      const wrapping = count > 1 && (position === 0 || position === count + 1);
      if (wrapping) root.classList.add('is-rebasing');
      if (count > 1) {
        if (position === 0) position = count;
        else if (position === count + 1) position = 1;
      }
      paint();
      if (wrapping) {
        void track.offsetWidth;
        root.classList.remove('is-rebasing');
      }
    }

    function move(nextPosition) {
      if (count < 2) return;
      // Finish an interrupted wrap before starting another navigation action.
      settle();
      if (viewport.contains(document.activeElement) && document.activeElement !== viewport) {
        viewport.focus({ preventScroll: true });
      }
      position = nextPosition();
      // Commit the settled transform before enabling the next transition.
      void track.offsetWidth;
      paint(true, true);
      if (reducedMotion.matches) settle();
      else timer = window.setTimeout(settle, 600);
    }

    root.querySelector('[data-review-prev]')?.addEventListener('click', () => move(() => position - 1), options);
    root.querySelector('[data-review-next]')?.addEventListener('click', () => move(() => position + 1), options);
    dots.forEach((dot, index) => dot.addEventListener('click', () => move(() => index + 1), options));
    root.addEventListener('keydown', (event) => {
      if (count < 2 || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowLeft') move(() => position - 1);
      if (event.key === 'ArrowRight') move(() => position + 1);
      if (event.key === 'Home') move(() => 1);
      if (event.key === 'End') move(() => count);
    }, options);
    track.addEventListener('transitionend', (event) => {
      if (event.target === track && event.propertyName === 'transform') settle();
    }, options);
    viewport.addEventListener('pointerdown', (event) => {
      swiped = false;
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      pointer = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, options);
    viewport.addEventListener('pointerup', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      pointer = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      swiped = true;
      move(() => position + (dx < 0 ? 1 : -1));
    }, options);
    viewport.addEventListener('pointercancel', () => { pointer = null; }, options);
    viewport.addEventListener('click', (event) => {
      if (!swiped) return;
      event.preventDefault();
      swiped = false;
    }, { ...options, capture: true });
    root.addEventListener('shopify:block:select', (event) => {
      const index = slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
      if (index >= 0 && count > 1) move(() => index + 1);
    }, options);

    let observedWidth = viewport.clientWidth;
    const observer = new ResizeObserver((entries) => {
      if (root.classList.contains('wp-reviews--photos')) {
        const widthChanged = observedWidth !== viewport.clientWidth;
        observedWidth = viewport.clientWidth;
        if (!widthChanged && !entries.some(entry => entry.target !== viewport)) return;
      }
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(settle);
    });
    observer.observe(viewport);
    if (root.classList.contains('wp-reviews--photos')) slides.forEach(slide => observer.observe(slide));
    paint();
    instances.set(root, () => {
      controller.abort();
      observer.disconnect();
      window.clearTimeout(timer);
      window.cancelAnimationFrame(resizeFrame);
      track.querySelectorAll('[data-review-clone]').forEach((item) => item.remove());
      viewport.style.removeProperty('height');
      track.style.removeProperty('transform');
      track.style.removeProperty('transition');
      slides.forEach((slide) => {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        slide.classList.remove('is-active');
      });
      root.classList.remove('is-ready');
      controls.hidden = true;
      instances.delete(root);
    });
  }

  function initializeWithin(scope) {
    if (scope.matches?.('[data-review-carousel]')) initialize(scope);
    scope.querySelectorAll('[data-review-carousel]').forEach(initialize);
  }

  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, root) => {
      if (event.target === root || event.target.contains(root)) destroy();
    });
  });
})();
